import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { CheckCircle2, RefreshCw } from 'lucide-react';
import { apiPost } from '../../lib/api.js';
import { fmtDate } from '../../lib/format.js';
import ResyncConfirmModal from './ResyncConfirmModal.jsx';

const AGENCY_DEFAULT = 'agency_default';

/**
 * "Currently synced" badge + Re-Sync actions for an agent who already synced.
 * Every re-sync goes through ResyncConfirmModal before the request is sent.
 * @param {Object} props
 * @param {{syncedFrom?: string, syncedAt?: string, syncedManagerName?: string}} props.status - GET /sync/status payload.
 * @param {Array<{_id: string, name?: string, email?: string}>} [props.managers]
 * @returns {JSX.Element}
 */
export default function ResyncPanel({ status, managers = [] }) {
  const qc = useQueryClient();
  const [target, setTarget] = useState(null); // { id, name } while the modal is open

  const fromManager = managers.find((m) => String(m._id) === String(status?.syncedFrom));
  const managerName = status?.syncedManagerName || fromManager?.name || 'Team Manager';

  const resync = useMutation({
    mutationFn: ({ id, syncMode }) => apiPost('/sync/resync', {
      targetManagerId: id,
      syncMode,
      syncedCategories: ['sms_drips', 'email_templates', 'ai_bots', 'tags'],
    }),
    onSuccess: (res) => {
      toast.success(res?.message || `Templates successfully updated to latest version from ${target?.name}`);
      setTarget(null);
      ['sync-status', 'drips', 'text-bots'].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
    },
    onError: (err) => toast.error(err?.response?.data?.error || err.message || 'Re-sync failed'),
  });

  return (
    <>
      <div className="p-4 rounded-xl border border-emerald-500/30 bg-emerald-500/10 space-y-3" data-testid="resync-panel">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0" />
            <div>
              <div className="font-bold text-emerald-200 text-sm">
                Currently Synced to {managerName}{status?.syncedAt ? ` on ${fmtDate(status.syncedAt)}` : ''}
              </div>
              <div className="text-emerald-200/70 text-xs">
                Your CRM is operational with pre-built cadences and booking bots.
              </div>
            </div>
          </div>
          <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 font-mono">Active</span>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            className="btn-secondary flex items-center gap-2 text-sm"
            onClick={() => setTarget({ id: status?.syncedFrom || status?.directManagerId, name: managerName })}
            disabled={!status?.syncedFrom && !status?.directManagerId}
          >
            <RefreshCw className="w-4 h-4" /> Re-Sync with Manager
          </button>
          <button
            type="button"
            className="text-xs text-civix-cyan underline underline-offset-2 hover:text-white"
            onClick={() => setTarget({ id: AGENCY_DEFAULT, name: 'the Agency Default' })}
          >
            Switch to Agency Default Templates
          </button>
        </div>
      </div>

      {target && (
        <ResyncConfirmModal
          sourceName={target.name}
          busy={resync.isPending}
          onCancel={() => setTarget(null)}
          onConfirm={(syncMode) => resync.mutate({ id: target.id, syncMode })}
        />
      )}
    </>
  );
}
